import { Quality } from '../../commands/practitioner/enums/CallQuality';

export interface PreflightSummary {
  latency: { avg: number | null; max: number | null };
  jitter: { avg: number | null; max: number | null };
  loss: { avgPct: number | null; maxPct: number | null };
  bitrate: { avgKbps: number | null; maxKbps: number | null };
}

const round = (n: number | null | undefined, p = 0) =>
  n == null || Number.isNaN(n) ? null : Math.round(n * 10 ** p) / 10 ** p;

// Normalize to milliseconds (Twilio jitter may be seconds)
const toMs = (n?: number | null) =>
  typeof n !== 'number' || Number.isNaN(n) ? null : n < 1 ? Math.round(n * 1000) : Math.round(n);

const toKbps = (n?: number | null) => (typeof n !== 'number' || Number.isNaN(n) ? null : Math.round(n));

// --- grading -> return Quality enum (Excellent/Good/Fair/Poor/Error) ---
export const gradeLatency = (msVal?: number | null): Quality =>
  msVal == null
    ? Quality.Error
    : msVal < 50
    ? Quality.Excellent
    : msVal <= 100
    ? Quality.Good
    : msVal <= 200
    ? Quality.Fair
    : Quality.Poor;

export const gradeJitter = (msVal?: number | null): Quality =>
  msVal == null ? Quality.Error : msVal < 10 ? Quality.Excellent : msVal <= 40 ? Quality.Good : Quality.Fair;

export const gradeLoss = (pct?: number | null): Quality =>
  pct == null
    ? Quality.Error
    : pct < 1
    ? Quality.Excellent
    : pct <= 2
    ? Quality.Good
    : pct <= 3
    ? Quality.Fair
    : Quality.Poor;

export const gradeBitrate = (kbps?: number | null): Quality =>
  kbps == null
    ? Quality.Error
    : kbps > 2500
    ? Quality.Excellent
    : kbps > 1500
    ? Quality.Good
    : kbps >= 800
    ? Quality.Fair
    : Quality.Poor;

// Order for picking “worse” of two grades
const ord: Record<Quality, number> = {
  [Quality.Excellent]: 0,
  [Quality.Good]: 1,
  [Quality.Fair]: 2,
  [Quality.Poor]: 3,
  // non-grade states treated as worst
  [Quality.Error]: 4,
  [Quality.Idle]: 5,
  [Quality.Testing]: 5,
};

const worse = (a: Quality, b: Quality): Quality => (ord[a] >= ord[b] ? a : b);

// --- summarize preflight report to numbers we display ---
export function summarizePreflight(r: any): PreflightSummary {
  const latencyAvg = toMs(r?.stats?.rtt?.average);
  const latencyMax = toMs(r?.stats?.rtt?.max) ?? latencyAvg;

  const jitterAvg = toMs(r?.stats?.jitter?.average);
  const jitterMax = toMs(r?.stats?.jitter?.max) ?? jitterAvg;

  const lossAvgPct = typeof r?.stats?.packetLoss?.average === 'number' ? round(r.stats.packetLoss.average, 1) : null;
  const lossMaxPct = typeof r?.stats?.packetLoss?.max === 'number' ? round(r.stats.packetLoss.max, 1) : lossAvgPct;
  const bitrateAvgKbps = toKbps(r?.bitrate?.averageBitrate);
  const bitrateValues = Array.isArray(r?.bitrate?.values) ? (r.bitrate.values as number[]) : [];
  const bitrateMaxKbps = bitrateValues.length > 0 ? Math.round(Math.max(...bitrateValues)) : bitrateAvgKbps;

  return {
    latency: { avg: latencyAvg, max: latencyMax },
    jitter: { avg: jitterAvg, max: jitterMax },
    loss: { avgPct: lossAvgPct, maxPct: lossMaxPct },
    bitrate: { avgKbps: bitrateAvgKbps, maxKbps: bitrateMaxKbps },
  };
}

// Overall quality from preflight
export function qualityFromPreflight(r: any): Quality {
  const s = summarizePreflight(r);
  const hasCoreStats = s.latency.avg != null && s.jitter.avg != null && s.loss.avgPct != null;

  if (s.loss.avgPct != null && s.loss.avgPct > 3) return Quality.Poor;
  if (s.bitrate.avgKbps != null && s.bitrate.avgKbps < 800) return Quality.Poor;
  if (s.jitter.avg != null && s.jitter.avg > 40) return Quality.Fair;

  if (s.bitrate.avgKbps != null) {
    if (
      s.bitrate.avgKbps > 2500 &&
      s.loss.avgPct != null &&
      s.loss.avgPct < 1 &&
      s.jitter.avg != null &&
      s.jitter.avg < 10 &&
      s.latency.avg != null &&
      s.latency.avg < 50
    ) {
      return Quality.Excellent;
    }
    return gradeBitrate(s.bitrate.avgKbps);
  }

  if (!hasCoreStats) return Quality.Error;
  return worse(gradeLatency(s.latency.avg), worse(gradeJitter(s.jitter.avg), gradeLoss(s.loss.avgPct)));
}

// Helpers you already use elsewhere
export const isBadQuality = (q: Quality) => q === Quality.Fair || q === Quality.Poor || q === Quality.Error;

// Text for the plain-string popup (if needed)
const show = (n: number | null) => (n == null ? '—' : String(n));
const labelFor = (q: Quality) =>
  q === Quality.Excellent
    ? 'Excellent'
    : q === Quality.Good
    ? 'Good'
    : q === Quality.Fair
    ? 'Fair'
    : q === Quality.Poor
    ? 'Poor'
    : '—';

export function buildReportTextFromPreflight(r: any): string {
  if (!r) return '';
  const s = summarizePreflight(r);
  return [
    'Latency (ms) avg/max',
    `${show(s.latency.avg)} / ${show(s.latency.max)} (${labelFor(gradeLatency(s.latency.avg))})`,
    '',
    'Jitter (ms) avg/max',
    `${show(s.jitter.avg)} / ${show(s.jitter.max)} (${labelFor(gradeJitter(s.jitter.avg))})`,
    '',
    'Packet loss (%) avg/max',
    `${show(s.loss.avgPct)} / ${show(s.loss.maxPct)} (${labelFor(gradeLoss(s.loss.avgPct))})`,
    '',
    'Bitrate (kbps) avg/max',
    `${show(s.bitrate.avgKbps)} / ${show(s.bitrate.maxKbps)} (${labelFor(gradeBitrate(s.bitrate.avgKbps))})`,
  ].join('\n');
}
